import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useDeleteSchedule,
  useSetDefaultSchedule,
} from "@/features/calendar/hooks/use-schedules";
import type { Schedule } from "@/features/calendar/types";
import {
  CaretCircleDoubleUpIcon,
  DotsThreeOutlineIcon,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { toast } from "sonner";
import { EditScheduleDialog } from "./edit-dialog";

export const Action = ({ schedule }: { schedule: Schedule }) => {
  const { t } = useTranslation("schedules");
  const deleteSchedule = useDeleteSchedule();
  const setDefaultSchedule = useSetDefaultSchedule();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteSchedule.mutateAsync(schedule.id);
      toast.success(t("messages.deleted"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.deleteError"),
      );
    }
  };

  const handleSetDefault = async () => {
    try {
      await setDefaultSchedule.mutateAsync(schedule.id);
      toast.success(t("messages.defaultUpdated"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("messages.defaultUpdateError"),
      );
    }
  };

  return (
    <>
      <div className="text-right flex items-center justify-end gap-2">
        <Button variant="ghost" size="icon" onClick={() => setIsEditOpen(true)}>
          <PencilSimple className="h-4 w-4" weight="fill" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">{t("common.openMenu")}</span>
              <DotsThreeOutlineIcon weight="fill" className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!schedule.is_default && (
              <DropdownMenuItem onClick={handleSetDefault}>
                <CaretCircleDoubleUpIcon className="h-4 w-4" weight="fill" />
                {t("actions.makeDefault")}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="p-0">
              <Button
                variant="ghost"
                onClick={handleDelete}
                className="w-full justify-start text-destructive hover:text-destructive"
                confirm={t("messages.deleteConfirm")}
              >
                <Trash className="h-4 w-4" weight="fill" />
                {t("actions.delete")}
              </Button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <EditScheduleDialog
        schedule={schedule}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      />
    </>
  );
};
